import { User } from "firebase/auth";
import React, { createContext, useEffect, useState } from "react";
import { AppShellFallback } from "~/components/Layout/Fallbacks";
import { auth } from "~/lib/firebase";
import { getTenantInfoFromApi } from "~/services/server/user-tenant/getTenantInfo";
import { mapFirestoreTenantProfileToDisplay } from "~/utils/mapFirestoreTenantProfileToDisplay";
import type { FirestoreTenantProfileDisplay } from "~/utils/mapFirestoreTenantProfileToDisplay";
import * as APITypes from "~/API";

export const userContext = createContext<{
  status: APITypes.TenantStatusType;
  authenticated: boolean;
  isDoneWithBackground: boolean;
  setIsDoneWithBackground: React.Dispatch<React.SetStateAction<boolean>>;
  profile: FirestoreTenantProfileDisplay | null;
}>({
  status: APITypes.TenantStatusType.InActive,
  authenticated: false,
  isDoneWithBackground: false,
  setIsDoneWithBackground: () => {},
  profile: null,
});

export const ContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [status, setStatus] = useState<APITypes.TenantStatusType>(
    APITypes.TenantStatusType.InActive,
  );
  const [authenticated, setAuthenticated] = useState<boolean>(false);
  const [isDoneWithBackground, setIsDoneWithBackground] =
    useState<boolean>(false);
  const [profile, setProfile] =
    useState<FirestoreTenantProfileDisplay | null>(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user: User | null) => {
      // console.log("user", user);
      if (!user) {
        setAuthenticated(false);
        setStatus(APITypes.TenantStatusType.InActive);
        setIsDoneWithBackground(false);
        setProfile(null);
      } else {
        const tenantInfo = await getTenantInfoFromApi();
        setAuthenticated(true);
        setStatus(
          tenantInfo.data?.status || APITypes.TenantStatusType.InActive,
        );
        setIsDoneWithBackground(!!tenantInfo.data?.income);
        setProfile(mapFirestoreTenantProfileToDisplay(tenantInfo.profile));
      }
      setIsLoading(false);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  if (isLoading) {
    return <AppShellFallback />;
  }

  return (
    <userContext.Provider
      value={{
        status,
        authenticated,
        isDoneWithBackground,
        setIsDoneWithBackground,
        profile,
      }}
    >
      {children}
    </userContext.Provider>
  );
};
